import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, MapPinIcon, PhoneIcon, UserIcon, CalendarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import MainLayout from '../components/Layout/MainLayout';
import { showToast } from '../components/UI';
import Badge from '../components/UI/Badge';
import enquiryService, { Enquiry } from '../services/enquiryService';
import { rigService } from '../services/rigService';
import { Rig } from '../types/rig';

type ViewMode = 'month' | 'day';

const ServiceCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedEnquiries, setSelectedEnquiries] = useState<Enquiry[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [rigs, setRigs] = useState<Rig[]>([]);
  const [filters, setFilters] = useState({
    dayOfWeek: '',
    rigId: ''
  });

  // Get first and last day of current month
  const getMonthRange = (date: Date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return { firstDay, lastDay };
  };

  // Load enquiries for the current month
  const loadEnquiries = async () => {
    try {
      setLoading(true);
      const { firstDay, lastDay } = getMonthRange(currentDate);

      // Format dates in local timezone to avoid timezone conversion issues
      const formatLocalDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const response = await enquiryService.getEnquiries({
        serviceDateFrom: formatLocalDate(firstDay),
        serviceDateTo: formatLocalDate(lastDay),
        limit: 1000 // Get all enquiries for the month
      });

      setEnquiries(response.enquiries);
    } catch (error) {
      showToast.error('Failed to load enquiries');
      console.error('Error loading enquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load rigs
  const loadRigs = async () => {
    try {
      const response = await rigService.getRigs({ limit: 1000 });
      console.log('Rigs response:', response);
      if (response.data && Array.isArray(response.data)) {
        setRigs(response.data);
        console.log('Rigs loaded:', response.data.length);
      } else {
        console.error('Unexpected rigs response format:', response);
      }
    } catch (error) {
      console.error('Error loading rigs:', error);
      showToast.error('Failed to load rigs');
    }
  };

  useEffect(() => {
    loadEnquiries();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  useEffect(() => {
    loadRigs();
  }, []);

  // Get enquiries for a specific date with filters
  const getEnquiriesForDate = (date: Date): Enquiry[] => {
    // Format date as YYYY-MM-DD in local timezone to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;

    return enquiries.filter(enquiry => {
      if (!enquiry.service_date) return false;
      // Extract just the date part without timezone conversion
      const serviceDate = enquiry.service_date.split('T')[0];
      if (serviceDate !== dateString) return false;

      // Apply filters
      if (filters.dayOfWeek && date.getDay().toString() !== filters.dayOfWeek) {
        return false;
      }

      if (filters.rigId && enquiry.rig_id?.toString() !== filters.rigId) {
        return false;
      }

      return true;
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      dayOfWeek: '',
      rigId: ''
    });
  };

  // Check if any filter is active
  const hasActiveFilters = filters.dayOfWeek !== '' || filters.rigId !== '';

  // Generate color for rig based on rig ID
  const getRigColor = (rigId: number | null | undefined) => {
    if (!rigId) {
      return {
        bg: 'bg-gray-100',
        text: 'text-gray-700',
        border: 'border-gray-500'
      };
    }

    const colors = [
      { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-500' },
      { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-500' },
      { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-500' },
      { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-500' },
      { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-500' },
      { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-500' },
      { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-500' },
      { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-500' },
      { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-600' },
      { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-500' },
    ];

    // Use rigId to consistently assign the same color to the same rig
    const colorIndex = rigId % colors.length;
    return colors[colorIndex];
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days: (Date | null)[] = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  // Navigate to previous month
  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  // Navigate to next month
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  // Go to today
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Handle date click - can open modal or switch to day view
  const handleDateClick = (date: Date, openModal: boolean = true) => {
    if (openModal) {
      setSelectedDate(date);
      const dayEnquiries = getEnquiriesForDate(date);
      setSelectedEnquiries(dayEnquiries);
    } else {
      // Switch to day view for this date
      setCurrentDate(date);
      setViewMode('day');
    }
  };

  // Close modal
  const closeModal = () => {
    setSelectedDate(null);
    setSelectedEnquiries([]);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const calendarDays = generateCalendarDays();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <MainLayout>
      <div className="h-full overflow-y-auto">
        <div className="p-6 space-y-6 min-h-full">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Service Calendar</h1>
            <p className="text-gray-600 mt-1">View scheduled services by date</p>
          </div>

          {/* Combined Filters and Legend */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex flex-col lg:flex-row items-start gap-4 lg:gap-6">
              {/* Rig Color Legend */}
              {rigs.length > 0 && (
                <div className="w-full lg:flex-1">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Rig Colors</h3>
                  <div className="flex flex-wrap gap-2 lg:gap-3">
                    {rigs.map((rig) => {
                      const color = getRigColor(rig.id);
                      return (
                        <div
                          key={rig.id}
                          className="flex items-center gap-1.5"
                        >
                          <div className={`w-3 h-3 rounded border-l-2 ${color.bg} ${color.border}`}></div>
                          <span className="text-xs text-gray-700">{rig.rig_no}</span>
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded border-l-2 bg-gray-100 border-gray-500"></div>
                      <span className="text-xs text-gray-700">No Rig</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="w-full lg:flex-shrink-0 lg:w-96">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                    >
                      <XMarkIcon className="w-3 h-3" />
                      Clear All
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Day of Week Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Day of Week
                    </label>
                    <select
                      value={filters.dayOfWeek}
                      onChange={(e) => setFilters({ ...filters, dayOfWeek: e.target.value })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Days</option>
                      <option value="0">Sunday</option>
                      <option value="1">Monday</option>
                      <option value="2">Tuesday</option>
                      <option value="3">Wednesday</option>
                      <option value="4">Thursday</option>
                      <option value="5">Friday</option>
                      <option value="6">Saturday</option>
                    </select>
                  </div>

                  {/* Rig Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Rig
                    </label>
                    <select
                      value={filters.rigId}
                      onChange={(e) => setFilters({ ...filters, rigId: e.target.value })}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Rigs</option>
                      {rigs.map((rig) => (
                        <option key={rig.id} value={rig.id}>
                          {rig.rig_no}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Controls */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
            <button
              onClick={viewMode === 'month' ? previousMonth : () => setCurrentDate(new Date(currentDate.getTime() - 24 * 60 * 60 * 1000))}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeftIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
            </button>

            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 text-center">
                {viewMode === 'month'
                  ? `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                  : currentDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                }
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={goToToday}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  Today
                </button>

                {/* View Toggle */}
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('month')}
                    className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium rounded transition-colors ${
                      viewMode === 'month'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Month
                  </button>
                  <button
                    onClick={() => setViewMode('day')}
                    className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium rounded transition-colors ${
                      viewMode === 'day'
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Day
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={viewMode === 'month' ? nextMonth : () => setCurrentDate(new Date(currentDate.getTime() + 24 * 60 * 60 * 1000))}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRightIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
            </button>
          </div>

          {/* Calendar Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : viewMode === 'month' ? (
            <>
            {/* Desktop Calendar Grid */}
            <div className="hidden md:block border border-gray-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
                {/* Day headers */}
                {dayNames.map((day) => (
                  <div
                    key={day}
                    className="text-center text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 border-r border-gray-200 last:border-r-0"
                  >
                    <span className="hidden sm:inline">{day}</span>
                    <span className="sm:hidden">{day.substring(0, 1)}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {/* Calendar days */}
                {calendarDays.map((date, index) => {
                  if (!date) {
                    return (
                      <div
                        key={`empty-${index}`}
                        className="h-20 sm:h-24 lg:h-28 bg-gray-50 border-r border-b border-gray-200"
                      />
                    );
                  }

                  const dayEnquiries = getEnquiriesForDate(date);
                  const isToday = date.getTime() === today.getTime();
                  const hasEnquiries = dayEnquiries.length > 0;
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                  return (
                    <div
                      key={index}
                      onClick={() => hasEnquiries && handleDateClick(date)}
                      className={`
                        h-20 sm:h-24 lg:h-28 p-1 sm:p-1.5 lg:p-2 border-r border-b border-gray-200 transition-all overflow-hidden relative
                        ${hasEnquiries ? 'cursor-pointer hover:bg-blue-50' : ''}
                        ${isToday ? 'bg-blue-50' : isWeekend ? 'bg-gray-50' : 'bg-white'}
                      `}
                    >
                      <div className="h-full flex flex-col">
                        <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                          <div className={`
                            text-xs sm:text-sm font-medium w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full
                            ${isToday ? 'bg-blue-600 text-white' : isWeekend ? 'text-gray-500' : 'text-gray-700'}
                          `}>
                            {date.getDate()}
                          </div>
                          {hasEnquiries && (
                            <div className="text-[9px] sm:text-[10px] font-semibold text-gray-500">
                              {dayEnquiries.length}
                            </div>
                          )}
                        </div>
                        {hasEnquiries && (
                          <div className="flex-1 overflow-hidden flex flex-col gap-0.5 sm:gap-1">
                            {dayEnquiries.slice(0, window.innerWidth < 640 ? 1 : 2).map(enquiry => {
                              const rigColor = getRigColor(enquiry.rig_id);
                              const rigName = enquiry.rig?.rig_no || 'No Rig';
                              const tooltipText = `${enquiry.contact_person || 'N/A'} - ${rigName}`;

                              return (
                                <div
                                  key={enquiry.id}
                                  className={`text-[8px] sm:text-[10px] px-1 sm:px-1.5 py-0.5 sm:py-1 rounded truncate border-l-2 ${rigColor.bg} ${rigColor.text} ${rigColor.border}`}
                                  title={tooltipText}
                                >
                                  <span className="font-medium">{enquiry.contact_person || 'N/A'}</span>
                                </div>
                              );
                            })}
                            {dayEnquiries.length > (window.innerWidth < 640 ? 1 : 2) && (
                              <div className="text-[8px] sm:text-[9px] text-gray-500 px-1 font-medium">
                                +{dayEnquiries.length - (window.innerWidth < 640 ? 1 : 2)} more
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile List View */}
            <div className="md:hidden space-y-3">
              {calendarDays.filter(date => date !== null).map((date, index) => {
                if (!date) return null;

                const dayEnquiries = getEnquiriesForDate(date);
                const isToday = date.getTime() === today.getTime();
                const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                return (
                  <div
                    key={index}
                    className={`bg-white border rounded-lg overflow-hidden ${
                      isToday ? 'border-blue-500 shadow-md' : 'border-gray-200'
                    }`}
                  >
                    {/* Date Header */}
                    <div className={`px-4 py-3 flex items-center justify-between ${
                      isToday ? 'bg-blue-50' : isWeekend ? 'bg-gray-50' : 'bg-white'
                    }`}>
                      <div className="flex items-center gap-3">
                        <div className={`text-center ${
                          isToday ? 'text-blue-600' : isWeekend ? 'text-gray-500' : 'text-gray-700'
                        }`}>
                          <div className="text-xs font-medium uppercase">
                            {dayNames[date.getDay()].substring(0, 3)}
                          </div>
                          <div className={`text-2xl font-bold ${
                            isToday ? 'bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center' : ''
                          }`}>
                            {date.getDate()}
                          </div>
                        </div>
                        {dayEnquiries.length > 0 && (
                          <div className="text-sm text-gray-600">
                            {dayEnquiries.length} service{dayEnquiries.length > 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Services List */}
                    {dayEnquiries.length > 0 && (
                      <div className="divide-y divide-gray-100">
                        {dayEnquiries.map(enquiry => {
                          const rigColor = getRigColor(enquiry.rig_id);
                          const rigName = enquiry.rig?.rig_no || 'No Rig';

                          return (
                            <div
                              key={enquiry.id}
                              className="px-4 py-3"
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-1 h-full min-h-[40px] rounded ${rigColor.bg} border-l-4 ${rigColor.border}`}></div>
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">
                                    {enquiry.contact_person || 'N/A'}
                                  </div>
                                  {enquiry.company_name && (
                                    <div className="text-sm text-gray-600 mt-0.5">
                                      {enquiry.company_name}
                                    </div>
                                  )}
                                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                    <span className={`${rigColor.text} font-medium`}>{rigName}</span>
                                    {enquiry.contact_no && (
                                      <span className="flex items-center gap-1">
                                        <PhoneIcon className="w-3 h-3" />
                                        {enquiry.contact_no}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div>
                                  {enquiry.is_confirmed === 'YES' && (
                                    <Badge variant="success">Confirmed</Badge>
                                  )}
                                  {enquiry.is_cancelled === 'YES' && (
                                    <Badge variant="error">Cancelled</Badge>
                                  )}
                                  {enquiry.is_confirmed !== 'YES' && enquiry.is_cancelled !== 'YES' && (
                                    <Badge variant="warning">Pending</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            </>
          ) : (
            /* Day View */
            <div className="space-y-4">
              {getEnquiriesForDate(currentDate).length === 0 ? (
                <div className="text-center py-12">
                  <CalendarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No services scheduled for this day</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {getEnquiriesForDate(currentDate).map(enquiry => (
                    <div
                      key={enquiry.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer bg-white"
                      onClick={() => navigate('/enquiries')}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900 text-lg">
                            {enquiry.contact_person || 'N/A'}
                          </h4>
                          {enquiry.company_name && (
                            <p className="text-sm text-gray-600">{enquiry.company_name}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Enquiry No: {enquiry.enquiry_no || 'N/A'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {enquiry.is_confirmed === 'YES' && (
                            <Badge variant="success">Confirmed</Badge>
                          )}
                          {enquiry.is_cancelled === 'YES' && (
                            <Badge variant="error">Cancelled</Badge>
                          )}
                          {enquiry.is_confirmed !== 'YES' && enquiry.is_cancelled !== 'YES' && (
                            <Badge variant="warning">Pending</Badge>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {enquiry.contact_no && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <PhoneIcon className="w-4 h-4" />
                            <span>{enquiry.contact_no}</span>
                          </div>
                        )}
                        {(enquiry.town || enquiry.district) && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPinIcon className="w-4 h-4" />
                            <span>{[enquiry.town, enquiry.district].filter(Boolean).join(', ')}</span>
                          </div>
                        )}
                        {enquiry.assignedUser && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <UserIcon className="w-4 h-4" />
                            <span>Manager: {enquiry.assignedUser.name}</span>
                          </div>
                        )}
                        {enquiry.assignedFieldTeam && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <UserIcon className="w-4 h-4" />
                            <span>Field: {enquiry.assignedFieldTeam.name}</span>
                          </div>
                        )}
                        {enquiry.boreSize && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="font-medium">Bore Size:</span>
                            <span>{enquiry.boreSize.size}</span>
                          </div>
                        )}
                        {enquiry.rig && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="font-medium">Rig:</span>
                            <span>{enquiry.rig.rig_no}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Details Modal */}
        {selectedDate && selectedEnquiries.length > 0 && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
            onClick={closeModal}
          >
            <div
              className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8 flex flex-col max-h-[calc(100vh-4rem)]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between flex-shrink-0 rounded-t-lg">
                <div>
                  <h3 className="text-xl font-bold">
                    Services on {selectedDate.toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </h3>
                  <p className="text-blue-100 text-sm mt-1">
                    {selectedEnquiries.length} scheduled service{selectedEnquiries.length > 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-blue-500 rounded-lg transition-colors"
                  aria-label="Close modal"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="overflow-y-auto flex-1 p-6">
                <div className="space-y-4">
                  {selectedEnquiries.map(enquiry => (
                    <div
                      key={enquiry.id}
                      className="border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900 text-lg">
                            {enquiry.contact_person || 'N/A'}
                          </h4>
                          {enquiry.company_name && (
                            <p className="text-sm text-gray-600">{enquiry.company_name}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            Enquiry No: {enquiry.enquiry_no || 'N/A'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {enquiry.is_confirmed === 'YES' && (
                            <Badge variant="success">Confirmed</Badge>
                          )}
                          {enquiry.is_cancelled === 'YES' && (
                            <Badge variant="error">Cancelled</Badge>
                          )}
                          {enquiry.is_confirmed !== 'YES' && enquiry.is_cancelled !== 'YES' && (
                            <Badge variant="warning">Pending</Badge>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {enquiry.contact_no && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <PhoneIcon className="w-4 h-4" />
                            <span>{enquiry.contact_no}</span>
                          </div>
                        )}
                        {(enquiry.town || enquiry.district) && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <MapPinIcon className="w-4 h-4" />
                            <span>{[enquiry.town, enquiry.district].filter(Boolean).join(', ')}</span>
                          </div>
                        )}
                        {enquiry.assignedUser && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <UserIcon className="w-4 h-4" />
                            <span>Manager: {enquiry.assignedUser.name}</span>
                          </div>
                        )}
                        {enquiry.assignedFieldTeam && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <UserIcon className="w-4 h-4" />
                            <span>Field: {enquiry.assignedFieldTeam.name}</span>
                          </div>
                        )}
                        {enquiry.boreSize && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="font-medium">Bore Size:</span>
                            <span>{enquiry.boreSize.size}</span>
                          </div>
                        )}
                        {enquiry.rig && (
                          <div className="flex items-center gap-2 text-gray-600">
                            <span className="font-medium">Rig:</span>
                            <span>{enquiry.rig.rig_no}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </MainLayout>
  );
};

export default ServiceCalendar;
